import React, { useMemo, useState } from "react";
import { FilterOutlined, SearchOutlined } from "@ant-design/icons";
import { Input } from "antd5";
import { intersection, isEqual } from "lodash";

import ExportFrameworkSuppliersButton from "components/framework/ExportFrameworkSuppliersButton";
import Button from "components/ui/button/Button";
import { ClearSelectedButton } from "lib/core_components/ClearSelectedButton";
import EditColumnsButton from "lib/core_components/EditColumnsButton";
import { createUseDebounce } from "lib/debounce";
import { FrameworkDetails } from "lib/hooks/api/frameworks/useDescribeFramework";
import { useSearchFrameworkSuppliers } from "lib/hooks/api/frameworks/useSearchFrameworkOrganisations";
import { useURLState } from "lib/hooks/useURLState";
import { EventDataTypes } from "lib/tracking";
import { ColumnSetting } from "lib/types/models";
import { isDefined } from "lib/utils";
import { useTablePageSize } from "../../lib/hooks/useTablePageSize";
import FilterDrawer from "../filter_form/filter_drawer/FilterDrawer";
import { FilterForm, FilterFormProps } from "../suppliers/FilterForm";
import { QuickFilterBar } from "../suppliers/QuickFilterBar";
import { SaveSupplierButton } from "../suppliers/SaveSupplierButton";
import { SupplierTable } from "../suppliers/SupplierTable";
import { useSuppliersTracking } from "../suppliers/tracking";
import {
  AdditionalSupplierData,
  DEFAULT_FILTERS,
  parseSuppliersUrlState,
  SupplierColumns,
  SupplierFilters,
} from "../suppliers/utils";
import { ALL_SUPPLIER_COLUMNS, DEFAULT_SUPPLIER_COLUMNS } from "./utils";

import css from "./FrameworkSupplierTable.module.scss";

type Props = {
  frameworkData: FrameworkDetails;
  defaultFilters?: SupplierFilters;
  hiddenFilterSections?: FilterFormProps["hiddenSections"];
};

export function FrameworkSuppliersTable({
  frameworkData,
  defaultFilters = DEFAULT_FILTERS,
  hiddenFilterSections,
}: Props) {
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState(DEFAULT_SUPPLIER_COLUMNS);

  const [pageSize, setPageSize] = useTablePageSize("framework-suppliers-table");
  const [currentPage, setCurrentPage] = useState(1);

  const [urlFilters = defaultFilters, setFilters] = useURLState<SupplierFilters>(
    "supplierFilters",
    defaultFilters,
    parseSuppliersUrlState,
  );

  const [supplierSearch, setSupplierSearch] = useState(urlFilters.text);
  const [selectedSuppliers, setSelectedSuppliers] = useState<string[]>([]);
  const useDebounce = createUseDebounce(500);

  const debouncedSetFilters = useDebounce((f: SupplierFilters) => {
    // Work on a copy of the incoming filters to avoid mutating callers
    const updatedFilters = { ...f };
    const newMinusSort = { ...updatedFilters, sort: undefined };
    // If the only change from the default filters is the addition of a sort by name, remove that sort to avoid confusion
    const defaultMinusSort = { ...defaultFilters, sort: undefined };
    if (!isEqual(newMinusSort, defaultMinusSort) && f.sort?.field === "name") {
      updatedFilters.sort = { field: "awards_count", order: "DESC" };
    }

    setCurrentPage(1); // Reset to first page on filter change

    // Update filters
    setFilters(updatedFilters);
    setSupplierSearch(updatedFilters.text);
  });

  const tracking = useSuppliersTracking();
  const debouncedFreeSearchLog = useDebounce(() => tracking.freeSearchLog());

  const frameworkLots = frameworkData.lots;

  /**
   * In the case of filtering by the subset of suppliers within a framework, this useMemo makes sure that
   * the guids stay within the subset provided by the defaultFilters
   */
  const filtersWithSupplierGuids = useMemo((): SupplierFilters => {
    if (!frameworkLots || !frameworkData) {
      return urlFilters;
    }

    let lotsToFilterBy = frameworkLots;
    if (urlFilters.lotIds?.length) {
      lotsToFilterBy = frameworkLots.filter((lot) => urlFilters.lotIds?.includes(lot.id));
    }

    // This value will be used if we are filerting by lots, otherwise the full set of guids will be used
    const supplierGuidsInLots = lotsToFilterBy
      .map((lot) => lot.supplierGuids)
      .flat()
      .filter(isDefined);

    const guids = supplierGuidsInLots.length
      ? Array.from(new Set(supplierGuidsInLots))
      : frameworkData.suppliers?.map((s) => s.id) || [];

    // If there are lotsToFilterBy, then only select guids within those lots. Otherwise revert back to the
    // set of all supplier guids within this entire framework
    return {
      ...urlFilters,
      guids: urlFilters.guids ? intersection(urlFilters.guids, guids) : guids,
    };
  }, [frameworkData, frameworkLots, urlFilters]);

  const loggedFilterOpen = () => {
    setIsFilterDrawerOpen(true);
    tracking.filterDrawerOpened();
  };

  const { data: frameworkSupplierData, isLoading } = useSearchFrameworkSuppliers(
    frameworkData.id,
    {
      text: filtersWithSupplierGuids.text,
      isSme: filtersWithSupplierGuids.isSme === "" ? undefined : filtersWithSupplierGuids.isSme,
    },
    filtersWithSupplierGuids.sort || { field: "name", order: "ASC" },
    { current: 0, pageSize: 10000 },
  );

  const additionalData = React.useMemo<AdditionalSupplierData>(() => {
    if (!frameworkData.suppliers?.length || !frameworkSupplierData) {
      return {
        frameworkData: new Map(),
      };
    }
    const perSupplierData: AdditionalSupplierData["frameworkData"] = new Map();
    for (const supplier of frameworkData.suppliers) {
      const supplierFrameworkData = {
        frameworkId: frameworkData.id,
        callOffStats: frameworkSupplierData.organisations.find((org) => org.id === supplier.id)
          ?.callOffs,
        lots: frameworkData.lots.filter((lot) => lot.supplierGuids?.includes(supplier.id)),
      };

      perSupplierData.set(supplier.id, supplierFrameworkData);
    }

    return {
      frameworkData: perSupplierData,
    };
  }, [frameworkData, frameworkSupplierData]);

  return (
    <div className={css.container}>
      <div className={css.actionsContainer}>
        <div className={css.actions}>
          <Input
            placeholder="Search by supplier name"
            prefix={<SearchOutlined />}
            value={supplierSearch}
            onChange={(e) => {
              setSupplierSearch(e.target.value);
              debouncedSetFilters({ ...urlFilters, text: e.target.value });
              debouncedFreeSearchLog();
            }}
            allowClear
            className={css.searchInput}
          />
          <Button buttonType="secondary" onClick={loggedFilterOpen}>
            <FilterOutlined className={css.filterIcon} /> Filters
          </Button>
        </div>
        <div className={css.actions}>
          <div className={css.clearSelected}>
            {selectedSuppliers && selectedSuppliers.length > 0 && (
              <ClearSelectedButton
                count={selectedSuppliers.length}
                onClear={() => setSelectedSuppliers([])}
              />
            )}
          </div>
          {selectedSuppliers && selectedSuppliers.length === 0 && (
            <EditColumnsButton
              allAvailableColumns={ALL_SUPPLIER_COLUMNS.filter((f) => f.field !== "sme")}
              selectedColumns={selectedColumns}
              onNewColumnSettingChange={(c) =>
                setSelectedColumns([...(c as ColumnSetting<SupplierColumns>[])])
              }
              dataType={EventDataTypes.supplier}
            />
          )}
          <div className={css.saveSuppliers}>
            {selectedSuppliers.length > 0 && (
              <SaveSupplierButton
                supplierGuids={selectedSuppliers}
                trackingData={{ "Context source": "Bulk action" }}
              />
            )}
          </div>
          <ExportFrameworkSuppliersButton
            frameworkId={frameworkData.id}
            totalResults={filtersWithSupplierGuids.guids?.length || 0}
            selectedSupplierGuids={selectedSuppliers}
            isLoading={isLoading}
          />
        </div>
      </div>
      <QuickFilterBar
        filters={filtersWithSupplierGuids}
        pageSize={pageSize}
        currentPage={currentPage}
        onClearFilter={(key) => setFilters({ ...urlFilters, [key]: undefined })}
        onFilterClick={() => setIsFilterDrawerOpen(true)}
        hiddenFilterSections={hiddenFilterSections}
        clearAllFilters={() => setFilters(defaultFilters)}
      />

      <SupplierTable
        filters={filtersWithSupplierGuids}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        setPageSize={setPageSize}
        pageSize={pageSize}
        onSortChange={(sort) => setFilters({ ...urlFilters, sort })}
        selectedColumns={selectedColumns.map((f) => f.field)}
        selectedRows={selectedSuppliers}
        onSelectedRowsChange={setSelectedSuppliers}
        additionalData={additionalData}
      />
      <FilterDrawer open={isFilterDrawerOpen} onClose={() => setIsFilterDrawerOpen(false)}>
        {isFilterDrawerOpen && (
          <FilterForm
            closeDrawer={() => setIsFilterDrawerOpen(false)}
            onChange={debouncedSetFilters}
            filters={urlFilters}
            hiddenSections={hiddenFilterSections}
            frameworkLots={frameworkLots}
          />
        )}
      </FilterDrawer>
    </div>
  );
}
